import React, { useRef, useState } from 'react';
import { IconCheck, IconTag, IconX } from '@tabler/icons-react';
import { Controller, useForm } from 'react-hook-form';
import type { Group } from '../../../entities/group';
import type { Tag } from '../../../entities/tag';
import { pb } from '../../../shared/api/pocketbase';
import { getPbErrorMessage } from '../../../shared/lib/pbError';
import { logAction } from '../../../shared/lib/userLogger';
import { Button } from '../../../shared/ui/Button';
import { Input } from '../../../shared/ui/Input';
import { Select } from '../../../shared/ui/Select';
import { type TagForm, EMPTY_TAG_FORM, TAG_COLOR_PRESETS } from '../model/types';

interface TagModalProps {
  mode: 'add' | 'edit';
  initial?: Tag | null;
  groups: Group[];
  onClose: () => void;
  onSaved: () => void;
}

const TagModal: React.FC<TagModalProps> = ({ mode, initial, groups, onClose, onSaved }) => {
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const overlayRef = useRef<HTMLDivElement>(null);

  const singleGroup = mode === 'add' && groups.length === 1 ? groups[0] : null;

  const { control, formState: { errors }, handleSubmit, register, setValue, watch } = useForm<TagForm>({
    defaultValues: mode === 'edit' && initial
      ? {
          name: initial.name,
          groupId: initial.groupId ?? '',
          colorBg:   initial.colors?.bg   ?? '',
          colorText: initial.colors?.text ?? '',
          colorDot:  initial.colors?.dot  ?? '',
        }
      : singleGroup
        ? { ...EMPTY_TAG_FORM, groupId: singleGroup.id }
        : EMPTY_TAG_FORM,
  });

  const colorBg   = watch('colorBg');
  const colorText = watch('colorText');
  const colorDot  = watch('colorDot');
  const watchName = watch('name');

  const hasColor = Boolean(colorBg);

  const applyPreset = (preset: typeof TAG_COLOR_PRESETS[number]) => {
    setValue('colorBg',   preset.values.colorBg,   { shouldDirty: true });
    setValue('colorText', preset.values.colorText, { shouldDirty: true });
    setValue('colorDot',  preset.values.colorDot,  { shouldDirty: true });
  };

  const clearColor = () => {
    setValue('colorBg',   '', { shouldDirty: true });
    setValue('colorText', '', { shouldDirty: true });
    setValue('colorDot',  '', { shouldDirty: true });
  };

  const groupOptions = [
    { value: '', label: 'Без группы' },
    ...groups.map(g => ({ value: g.id, label: g.name || `Группа ${g.id.slice(-4)}` })),
  ];

  const handleFormSubmit = handleSubmit(async values => {
    setError('');
    setSaving(true);
    try {
      const data = {
        name: values.name.trim(),
        groupId: values.groupId || null,
        colors: values.colorBg
          ? { bg: values.colorBg, text: values.colorText, dot: values.colorDot }
          : null,
      };
      if (mode === 'add') {
        await pb.collection('tags').create(data);
        logAction(`Created tag: ${values.name.trim()}`, 'ADD');
      } else {
        await pb.collection('tags').update(initial!.id, data);
        logAction(`Edited tag: ${values.name.trim()}`, 'EDIT');
      }
      onSaved();
      onClose();
    } catch (err) {
      setError(getPbErrorMessage(err));
    } finally {
      setSaving(false);
    }
  });

  return (
    <div className="mo open" ref={overlayRef} onClick={e => e.target === overlayRef.current && onClose()}>
      <div className="cm cm-create" style={{ maxWidth: 460, overflow: 'visible' }}>
        <div className="cm-top">
          <div className="cm-ti">{mode === 'add' ? 'Создать тег' : 'Редактировать тег'}</div>
          <div className="cm-sp" />
          <button className="cm-x" onClick={onClose} type="button">
            <IconX size={16} stroke={2} />
          </button>
        </div>
        <form className="cm-create-body" onSubmit={handleFormSubmit} style={{ gap: 16, overflow: 'visible' }}>

          <div className="adm-field">
            <label className="adm-label">Название <span className="adm-req">*</span></label>
            <Controller
              control={control}
              name="name"
              rules={{
                required: 'Укажите название тега',
                validate: v => v.trim() !== '' || 'Укажите название тега',
              }}
              render={({ field }) => (
                <div className="adm-input-wrap">
                  <IconTag size={15} stroke={2} className="adm-input-icon" />
                  <Input {...field} className="adm-input" placeholder="Название тега..." autoFocus />
                </div>
              )}
            />
            {errors.name?.message && <div className="form-field-error">{errors.name.message}</div>}
          </div>

          {!singleGroup && (
            <div className="adm-field">
              <label className="adm-label">Группа</label>
              <Controller
                control={control}
                name="groupId"
                render={({ field }) => (
                  <Select
                    options={groupOptions}
                    value={field.value}
                    onChange={field.onChange}
                    placeholder="Без группы"
                  />
                )}
              />
            </div>
          )}

          <div className="adm-colors-section">
            <div className="adm-colors-header">
              <span className="adm-label">Цвет тега</span>
            </div>

            <div className="adm-color-presets">
              <button
                type="button"
                className={`adm-color-preset adm-tag-color-none${!hasColor ? ' active' : ''}`}
                title="Без цвета"
                onClick={clearColor}
              />
              {TAG_COLOR_PRESETS.map(preset => (
                <button
                  key={preset.dot}
                  type="button"
                  className={`adm-color-preset${colorDot === preset.dot ? ' active' : ''}`}
                  title={preset.label}
                  style={{ background: preset.dot }}
                  onClick={() => applyPreset(preset)}
                />
              ))}
            </div>

            <div
              className="adm-tag-color-preview"
              style={hasColor ? { background: colorBg } : undefined}
            >
              {hasColor && <span className="adm-tag-color-preview-dot" style={{ background: colorDot }} />}
              {!hasColor && <IconTag size={11} stroke={2} style={{ color: 'var(--text2)' }} />}
              <span style={hasColor ? { color: colorText, fontWeight: 700 } : undefined}>
                {watchName || 'Пример тега'}
              </span>
            </div>

            {hasColor && (
              <div className="adm-color-rows">
                {([
                  { field: 'colorBg',   label: 'Фон',        val: colorBg },
                  { field: 'colorDot',  label: 'Точка',      val: colorDot },
                  { field: 'colorText', label: 'Текст',      val: colorText },
                ] as const).map(({ field, label, val }) => (
                  <div key={field} className="adm-color-row">
                    <label className="adm-color-row-label">{label}</label>
                    <div className="adm-color-row-right">
                      <div className="adm-color-swatch" style={{ background: val }}>
                        <input type="color" className="adm-color-input" {...register(field)} />
                      </div>
                      <span className="adm-color-hex">{val}</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {error && <div className="adm-form-error">{error}</div>}
          <div className="adm-form-actions">
            <Button variant="adm-cancel" type="button" onClick={onClose}>Отмена</Button>
            <Button variant="adm-save" type="submit" disabled={saving}>
              {saving ? <span className="adm-btn-spinner" /> : <IconCheck size={15} stroke={2.5} />}
              {mode === 'add' ? 'Создать' : 'Сохранить'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export { TagModal };
